import React, {Component} from 'react';
import AdminReadyButton from '../../components/AdminReadyButton';
import strings from '../../strings';
import {getNonwordRepetition} from '../../constants/tests';
import {updateReady, updateStepData} from '../../api';
import {ENVIRONMENT} from '../../constants/index';

const NW_STAGES = {
	INTRO: 0,
	TRIAL: 1,
	WAITING_POST_TRIAL: 2,
	TEST: 3,
	WAITING: 4,
};

const TRIAL_ITEMS_COUNT = 2;
const DELAY_SECONDS = 10; // must match Test.js delay

class NonwordRepetitionAdmin extends Component {
	constructor(props) {
		super(props);

		const spec = getNonwordRepetition(
			this.props.testKeyType,
			this.props.ageGroup
		);

		this.trialItems =
			(spec &&
				spec.getTrialSteps(
					this.props.testKeyType,
					this.props.ageGroup
				)) ||
			[];
		this.testItems =
			(spec &&
				spec.getNonwordSteps(
					this.props.testKeyType,
					this.props.ageGroup
				)) ||
			[];

		this.state = {
			currentStage: props.currentStage || NW_STAGES.INTRO,
			currentStep: props.currentStep || 0,

			activePhase: 'immediate', // 'immediate' | 'delayed'

			results: {},
			trialResults: {},

			delayActive: false,
			delayRemaining: 0, // seconds
			disableDelayButtons: ENVIRONMENT === 'production' ? true : false,
		};

		this.rootEl = null;
		this.delayTimer = null; // handle for setInterval
	}

	componentDidMount() {
		updateReady(false);
		if (this.rootEl) this.rootEl.focus();
	}

	componentDidUpdate(prevProps) {
		// keep stage/step in sync with roomConfig coming from TestAdmin
		if (
			prevProps.currentStage !== this.props.currentStage ||
			prevProps.currentStep !== this.props.currentStep
		) {
			// New item or stage → reset admin delay timer
			if (this.delayTimer) {
				clearInterval(this.delayTimer);
				this.delayTimer = null;
			}

			this.setState({
				currentStage: this.props.currentStage,
				currentStep: this.props.currentStep,
				activePhase: 'immediate',
				delayActive: false,
				delayRemaining: 0,
				disableDelayButtons:
					ENVIRONMENT === 'production' ? true : false,
			});
		}
	}

	componentWillUnmount() {
		if (this.delayTimer) {
			clearInterval(this.delayTimer);
		}
	}

	codeToScore = (code) => (code === 'A' ? 1 : 0);

	setTrialScore = (phase, code) => {
		this.setState((prev) => {
			const {currentStep, currentStage} = prev;
			const prevItem = prev.trialResults[currentStep] || {};

			const isFirstImmediate =
				currentStage === NW_STAGES.TRIAL &&
				phase === 'immediate' &&
				(!prevItem.immediate || !prevItem.immediate.code);

			if (isFirstImmediate) {
				// tell candidate to start 10s distraction, and show admin timer
				updateStepData({nonwordImmediateDone: true});
				this.startDelayTimer();
			}
			const newPhaseObj = {
				code,
				score: this.codeToScore(code),
			};
			const merged = {
				...prevItem,
				[phase]: newPhaseObj,
			};
			const tot =
				(merged.immediate ? merged.immediate.score : 0) +
				(merged.delayed ? merged.delayed.score : 0);

			return {
				trialResults: {
					...prev.trialResults,
					[currentStep]: {...merged, total: tot},
				},
				activePhase: phase,
			};
		});
	};

	hasBothTrialAnswers = (idx) => {
		const item = this.state.trialResults[idx];
		return item && item.immediate && item.delayed;
	};

	goNextTrialItem = () => {
		const {currentStep} = this.state;
		const isLast = currentStep >= TRIAL_ITEMS_COUNT - 1;

		if (isLast) {
			// finished all 3 øvingsoppgaver → go to pause / test
			this.props.nextStage();
		} else {
			this.props.nextStep();
		}
	};

	setScore = (phase, code) => {
		this.setState((prev) => {
			const {currentStep, currentStage} = prev;
			const prevItem = prev.results[currentStep] || {};

			const isFirstImmediate =
				currentStage === NW_STAGES.TEST &&
				phase === 'immediate' &&
				(!prevItem.immediate || !prevItem.immediate.code);

			// side-effect: tell the server/candidate that immediate has been scored
			if (isFirstImmediate) {
				this.startDelayTimer();
				updateStepData({nonwordImmediateDone: true});
			}

			const newPhaseObj = {
				code,
				score: this.codeToScore(code),
			};
			const merged = {
				...prevItem,
				[phase]: newPhaseObj,
			};
			const tot =
				(merged.immediate ? merged.immediate.score : 0) +
				(merged.delayed ? merged.delayed.score : 0);

			return {
				results: {
					...prev.results,
					[currentStep]: {...merged, total: tot},
				},
				activePhase: phase,
			};
		});
	};

	hasBothAnswers = (idx) => {
		const item = this.state.results[idx];
		return item && item.immediate && item.delayed;
	};

	startDelayTimer = () => {
		// only used in TEST stage
		if (this.delayTimer) {
			clearInterval(this.delayTimer);
		}

		this.setState({
			delayActive: true,
			delayRemaining: DELAY_SECONDS,
		});

		this.delayTimer = setInterval(() => {
			this.setState((prev) => {
				const next = prev.delayRemaining - 1;
				if (next <= 0) {
					if (this.delayTimer) {
						clearInterval(this.delayTimer);
						this.delayTimer = null;
					}
					return {
						delayRemaining: 0,
						delayActive: false,
						disableDelayButtons: false,
					};
				}
				return {delayRemaining: next};
			});
		}, 1000);
	};

	stopDelayTimer = () => {
		if (this.delayTimer) {
			clearInterval(this.delayTimer);
			this.delayTimer = null;
		}
		this.setState({
			delayActive: false,
			delayRemaining: 0,
		});
	};

	goNextItem = () => {
		this.props.nextStep(this.state.results);
	};

	// ---------- hotkeys ----------

	handleKeyDown = (e) => {
		const key = (e.key || '').toLowerCase();
		const {currentStage, activePhase} = this.state;

		const isTrial = currentStage === NW_STAGES.TRIAL;
		const isTest = currentStage === NW_STAGES.TEST;

		// Only react to keys in trial or test stage.
		if (!isTrial && !isTest) return;

		const setScoreFn = isTrial ? this.setTrialScore : this.setScore;
		const hasBothFn = isTrial
			? this.hasBothTrialAnswers
			: this.hasBothAnswers;
		const goNextFn = isTrial ? this.goNextTrialItem : this.goNextItem;

		// A/S/D score on the active phase (immediate or delayed).
		if (key === 'a') {
			setScoreFn(activePhase, 'A');
			return;
		}
		if (key === 's') {
			setScoreFn(activePhase, 'S');
			return;
		}
		if (key === 'd') {
			setScoreFn(activePhase, 'D');
			return;
		}

		// 1 / 2 switch focus between immediate and delayed
		if (key === '1') {
			this.setState({activePhase: 'immediate'});
			return;
		}
		if (key === '2') {
			this.setState({activePhase: 'delayed'});
			return;
		}

		// Enter = next item (if both answers are scored)
		if (key === 'enter') {
			const {currentStep} = this.state;
			if (hasBothFn(currentStep)) {
				goNextFn();
			}
		}
	};

	renderIntro = () => {
		const {testKeyType = null} = this.props;
		return (
			<>
				<p>{strings.nonword_admin_intro_1}</p>
				<p>{strings.nonword_admin_intro_2}</p>

				<p>{strings.nonword_admin_intro_4}</p>

				<AdminReadyButton
					isPractiseTasks
					remoteIsReady={this.props.candidateReady}
					onClick={this.props.nextStep}
					testKeyType={testKeyType}
				/>
			</>
		);
	};

	renderTrial = () => {
		const {
			currentStep,
			activePhase,
			trialResults,
			delayActive,
			delayRemaining,
			disableDelayButtons,
		} = this.state;

		const items = this.trialItems || [];
		const total = items.length;
		const word = items[currentStep] || '';

		const itemRes = trialResults[currentStep] || {};
		const imm = itemRes.immediate || {};
		const del = itemRes.delayed || {};
		const bothAnswered = this.hasBothTrialAnswers(currentStep);

		return (
			<div>
				<p>{strings.nonword_admin_trial_1}</p>
				<p className="hotkey-hint-group">
					{strings.hotkeyCorrectSpoonerism}<br />
					{strings.hotkeyWrongSpoonerism}<br />
					{strings.hotkeyButtonUnansweredSpoonerism}
				</p>
				<p className="sm-step-progress">
					{strings.formatString(
						strings.trialTaskNumOf,
						currentStep + 1,
						total
					)}
				</p>
				<div className="sm-expected">
					<strong>{strings.nonword_admin_expectedAnswer}</strong>
					<span className="sm-expected__text">{word}</span>
				</div>
				<p>{strings.nonword_admin_trial_2}</p>

				<div className="nw-admin-sections">
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'immediate'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4>{strings.nonword_immediate}</h4>
						<div className="nw-admin-buttons">
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('immediate', 'A')
								}
							>
								{strings.hotkeyCorrectSpoonerism}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'L' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('immediate', 'L')
								}
							>
								{strings.hotkeyButtonUnansweredSpoonerism}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'K' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('immediate', 'K')
								}
							>
								{strings.hotkeyWrongSpoonerism}
							</button>
						</div>
					</div>

					{/* Delayed repetition */}
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'delayed'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4 className="nw-admin-heading">
							<span>{strings.nonword_delayed}</span>

							{delayActive && (
								<span className="nw-admin-delay-pill">
									⏱ {delayRemaining}s
								</span>
							)}
						</h4>

						<div className="nw-admin-buttons">
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('delayed', 'A')
								}
							>
								{strings.hotkeyCorrectSpoonerism}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(del.code === 'S' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('delayed', 'S')
								}
								disabled={disableDelayButtons}
							>
								{strings.hotkeyButtonUnansweredSpoonerism}
							</button>
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'K' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('delayed', 'K')
								}
							>
								{strings.hotkeyWrongSpoonerism}
							</button>
						</div>
					</div>
				</div>

				<div className="nw-admin-footer">
					<p className="nw-admin-total">
						{strings.nonword_item_total || 'Poäng på uppgiften'}:{' '}
						{(itemRes.total != null ? itemRes.total : 0) +
							' / 2'}{' '}
					</p>
					<div className={`adminItemButtons`}>
						{' '}
						<button
							type="button"
							className="sm-btn sm-btn--primary"
							disabled={!bothAnswered}
							onClick={this.goNextTrialItem}
						>
							{currentStep + 1 < total
								? strings.nextTask
								: strings.nextTask}
						</button>
					</div>
				</div>
			</div>
		);
	};

	renderWaitingPostTrial = () => (
		<div>
			<p>{strings.nonword_admin_wait_post_trial}</p>
			<div className="lit-all-buttons-div">
				<button
					className="sm-btn sm-btn--primary"
					type="button"
					onClick={this.props.nextStage}
					style={{width: '100px'}}
				>
					{strings.startAdult}
				</button>
			</div>
		</div>
	);

	renderTest = () => {
		const {
			currentStep,
			activePhase,
			results,
			delayActive,
			delayRemaining,
			disableDelayButtons,
		} = this.state;

		const items = this.testItems || [];
		const total = items.length;
		const word = items[currentStep] || '';

		const itemRes = results[currentStep] || {};
		const imm = itemRes.immediate || {};
		const del = itemRes.delayed || {};

		const bothAnswered = this.hasBothAnswers(currentStep);

		return (
			<div>
				<p>{strings.nonword_admin_test_info_1}</p>
				<p className="hotkey-hint-group">
					{strings.hotkeyCorrectSpoonerism}<br />
					{strings.hotkeyWrongSpoonerism}<br />
					{strings.hotkeyButtonUnansweredSpoonerism}
				</p>

				<p className="sm-step-progress">
					{strings.formatString(
						strings.taskNumOf,
						currentStep + 1,
						total
					)}
				</p>
				<p>{strings.nonword_admin_test_info_2}</p>

				<div className="sm-expected">
					<strong>{strings.nonword_admin_expectedAnswer}</strong>
					<span className="sm-expected__text">{word}</span>
				</div>

				<div className="nw-admin-sections">
					{/* Immediate repetition */}
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'immediate'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4>{strings.nonword_immediate}</h4>
						<div className="nw-admin-buttons">
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('immediate', 'A')}
							>
								{strings.hotkeyCorrectSpoonerism}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'L' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('immediate', 'L')}
							>
								{strings.hotkeyButtonUnansweredSpoonerism}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'K' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('immediate', 'K')}
							>
								{strings.hotkeyWrongSpoonerism}
							</button>
						</div>
					</div>

					{/* Delayed repetition */}
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'delayed'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4 className="nw-admin-heading">
							<span>{strings.nonword_delayed}</span>

							{delayActive && (
								<span className="nw-admin-delay-pill">
									⏱ {delayRemaining}s
								</span>
							)}
						</h4>
						<div className="nw-admin-buttons">
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('delayed', 'A')}
							>
								{strings.hotkeyCorrectSpoonerism}
							</button>
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'L' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('delayed', 'L')}
							>
								{strings.hotkeyButtonUnansweredSpoonerism}
							</button>
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'K' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('delayed', 'K')}
							>
								{strings.hotkeyWrongSpoonerism}
							</button>
						</div>
					</div>
				</div>
				<p>{strings.nonword_admin_test_info_3}</p>

				<div className="nw-admin-footer">
					<p className="nw-admin-total">
						{strings.nonword_item_total}:{' '}
						{(itemRes.total != null ? itemRes.total : 0) + ' / 2'}
					</p>
					<div className={`adminItemButtons`}>
						<button
							type="button"
							className="sm-btn sm-btn--primary"
							disabled={!bothAnswered}
							onClick={this.goNextItem}
						>
							{strings.nextTask}
						</button>
					</div>
				</div>
			</div>
		);
	};

	renderWaiting = () => (
		<div>
			<p>
				{strings.formatString(
					strings.testDoneAdult4,
					strings.nw_testName
				)}
			</p>
			{/* <button
        type="button"
        className="sm-btn sm-btn--primary"
        onClick={() => this.props.submitResults(this.state.results)}
      >
        {strings.submitResults || 'Lagre resultater'}
      </button> */}
			<button
				type="button"
				className="sm-btn sm-btn--ghost"
				onClick={this.props.nextStage}
			>
				{strings.nextTest || 'Neste deltest'}
			</button>
		</div>
	);

	// ---------- main render ----------

	render() {
		const {currentStage} = this.state;

		return (
			<div
				className="lit-intro NonwordRepetitionAdmin"
				tabIndex={0}
				ref={(el) => (this.rootEl = el)}
				// onKeyDown={this.handleKeyDown}
			>
				<h2 className="lit-page-title">
					{strings.nw_testName || 'Nonordsrepetition'}
				</h2>

				{currentStage === NW_STAGES.INTRO && this.renderIntro()}
				{currentStage === NW_STAGES.TRIAL && this.renderTrial()}
				{currentStage === NW_STAGES.WAITING_POST_TRIAL &&
					this.renderWaitingPostTrial()}
				{currentStage === NW_STAGES.TEST && this.renderTest()}
				{currentStage === NW_STAGES.WAITING && this.renderWaiting()}
			</div>
		);
	}
}

export default NonwordRepetitionAdmin;
